import { Button, Card, CardContent, CardHeader, CardTitle } from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { PlusCircleIcon } from 'lucide-react';
import { useState } from 'react';

import { AutoCompleteInput } from '@/components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '@/constants';
import { useGetCustomersQuery } from '@/features/slice/customers/customer-api-slice';
import { useModal } from '@/hooks';

import { useCartContext } from './cart.context';

export const CustomerSelector = () => {
    const { openModal } = useModal();
    const [searchInputText, setSearchInputText] = useState('');
    const debouncedSearchText = useDebounce(searchInputText, 100);
    const { orderType, activeCart, updateCustomer } = useCartContext();
    const customer_type = orderType;
    const cartCustomer = activeCart?.data?.info?.customer;

    const {
        data: customers,
        isLoading,
        isFetching,
    } = useGetCustomersQuery({
        search: debouncedSearchText,
        customer_type: customer_type,
    });

    const options = customers?.data?.map((customer) => ({
        label: `${customer.name}`,
        value: customer.id,
    }));

    const isClearDisabled = !cartCustomer;

    const onValueChange = (value: { label: string; value: string }) => {
        updateCustomer(value.value);
    };

    const onInputChange = (input: string) => {
        setSearchInputText(input);
    };

    const handleClearCustomer = () => {
        updateCustomer(null);
    };

    const handleOpenNewCustomerModal = () => {
        openModal(MODAL_ROUTE.CREATE_CUSTOMER);
    };

    const renderCustomer = () => {
        if (!cartCustomer) {
            return (
                <div className="mx-auto py-4">
                    <p className="text-center">No customer selected!</p>
                </div>
            );
        }

        return (
            <div className="mt-2 space-y-1">
                <div className="flex justify-between">
                    <span>Customer Name: </span>
                    <span>{cartCustomer.name}</span>
                </div>
                <div className="flex justify-between">
                    <span>Customer Email: </span>
                    <span>{cartCustomer.email}</span>
                </div>
                <div className="flex justify-between">
                    <span>Customer Phone: </span>
                    <span>{cartCustomer.phone_number}</span>
                </div>
            </div>
        );
    };

    return (
        <Card>
            <CardHeader className="px-3 pb-0 pt-3">
                <CardTitle className=" flex items-center justify-between">
                    <div className={'flex items-center gap-1'}>
                        <span>Select Customer</span>
                        <Button
                            type={'button'}
                            variant={'link'}
                            size={'icon'}
                            onClick={handleOpenNewCustomerModal}
                        >
                            <PlusCircleIcon />
                        </Button>
                    </div>
                    <Button
                        onClick={handleClearCustomer}
                        type={'button'}
                        variant={'link'}
                        disabled={isClearDisabled}
                        size={'icon'}
                    >
                        Clear
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent className="px-3 py-3">
                <AutoCompleteInput
                    options={options || []}
                    emptyMessage="No match found!"
                    onValueChange={onValueChange}
                    placeholder={'Select customer'}
                    onInputChange={onInputChange}
                    isLoading={isLoading || isFetching}
                />

                <div>{renderCustomer()}</div>
            </CardContent>
        </Card>
    );
};

CustomerSelector.displayName = 'CustomerSelector';
